// ============================================
// Audio Tour Data for all cities
// ============================================

export interface TourStop {
  id: string;
  nameKey: string;
  icon: string;
  imageUrl: string;
  videoUrl?: string; // Short looping video (5-6s, muted) - falls back to imageUrl
  lat: number;
  lon: number;
  mapUrl: string;
  audioFile: {
    it: string;
    en: string;
  };
  transcriptKey: {
    it: string;
    en: string;
  };
}

export interface AudioTourConfig {
  citySlug: string;
  cityName: string;
  themeColor: string;
  gradientFrom: string;
  gradientTo: string;
  introTranscriptKey: {
    it: string;
    en: string;
  };
  introAudioFile: {
    it: string;
    en: string;
  };
  introVideoUrl?: string; // Short looping video for intro screen
  stops: TourStop[];
  // Walking route waypoints (intermediate GPS points between stops following real streets)
  walkingRoute?: [number, number][];
}

// ─── Mola di Bari Audio Tour ───
export const molaDiBariTour: AudioTourConfig = {
  citySlug: 'mola-di-bari',
  cityName: 'Mola di Bari',
  themeColor: '#0d9488',
  gradientFrom: 'from-teal-600',
  gradientTo: 'to-emerald-700',
  introTranscriptKey: {
    it: 'audioTour.molaDiBari.intro',
    en: 'audioTour.molaDiBari.intro',
  },
  introAudioFile: {
    it: '/audio/tours/mola-di-bari/it/00-intro.mp3',
    en: '/audio/tours/mola-di-bari/en/00-intro.mp3',
  },
  introVideoUrl: '/video/tours/mola-di-bari/intro.mp4',
  stops: [
    {
      id: 'lungomare',
      nameKey: 'audioTour.molaDiBari.stops.lungomare.name',
      icon: '🌅',
      imageUrl: '/images/cities/mola-di-bari/lungomare.jpg',
      lat: 41.0630,
      lon: 17.0905,
      mapUrl: 'https://maps.app.goo.gl/ggfSTa9DQGa5zsZb7',
      audioFile: {
        it: '/audio/tours/mola-di-bari/it/01-lungomare.mp3',
        en: '/audio/tours/mola-di-bari/en/01-lungomare.mp3',
      },
      transcriptKey: {
        it: 'audioTour.molaDiBari.stops.lungomare.transcript',
        en: 'audioTour.molaDiBari.stops.lungomare.transcript',
      },
    },
    {
      id: 'chiesa-matrice',
      nameKey: 'audioTour.molaDiBari.stops.chiesaMatrice.name',
      icon: '⛪',
      imageUrl: '/images/cities/mola-di-bari/chiesa-matrice.jpg',
      lat: 41.0621,
      lon: 17.0924,
      mapUrl: 'https://maps.app.goo.gl/5Vru9zj6nWKqLrNY8',
      audioFile: {
        it: '/audio/tours/mola-di-bari/it/02-chiesa-matrice.mp3',
        en: '/audio/tours/mola-di-bari/en/02-chiesa-matrice.mp3',
      },
      transcriptKey: {
        it: 'audioTour.molaDiBari.stops.chiesaMatrice.transcript',
        en: 'audioTour.molaDiBari.stops.chiesaMatrice.transcript',
      },
    },
    {
      id: 'castello-angioino',
      nameKey: 'audioTour.molaDiBari.stops.castelloAngioino.name',
      icon: '🏰',
      imageUrl: '/images/cities/mola-di-bari/castello-angioino.jpg',
      lat: 41.0624,
      lon: 17.0912,
      mapUrl: 'https://maps.app.goo.gl/CFdnaj3e5w4Q8XSG6',
      audioFile: {
        it: '/audio/tours/mola-di-bari/it/03-castello-angioino.mp3',
        en: '/audio/tours/mola-di-bari/en/03-castello-angioino.mp3',
      },
      transcriptKey: {
        it: 'audioTour.molaDiBari.stops.castelloAngioino.transcript',
        en: 'audioTour.molaDiBari.stops.castelloAngioino.transcript',
      },
    },
    {
      id: 'piazza-xx-settembre',
      nameKey: 'audioTour.molaDiBari.stops.piazzaXXSettembre.name',
      icon: '⛲',
      imageUrl: '/images/cities/mola-di-bari/piazza-xx-settembre.jpg',
      lat: 41.0614,
      lon: 17.0914,
      mapUrl: 'https://maps.app.goo.gl/rrtq8izWn3VYSwMR8',
      audioFile: {
        it: '/audio/tours/mola-di-bari/it/04-piazza-xx-settembre.mp3',
        en: '/audio/tours/mola-di-bari/en/04-piazza-xx-settembre.mp3',
      },
      transcriptKey: {
        it: 'audioTour.molaDiBari.stops.piazzaXXSettembre.transcript',
        en: 'audioTour.molaDiBari.stops.piazzaXXSettembre.transcript',
      },
    },
    {
      id: 'chiesa-maddalena',
      nameKey: 'audioTour.molaDiBari.stops.chiesaMaddalena.name',
      icon: '🕍',
      imageUrl: '/images/cities/mola-di-bari/chiesa-maddalena.jpg',
      lat: 41.0608,
      lon: 17.0907,
      mapUrl: 'https://maps.app.goo.gl/79Vf8FFu3Cqg9XKL8',
      audioFile: {
        it: '/audio/tours/mola-di-bari/it/05-chiesa-maddalena.mp3',
        en: '/audio/tours/mola-di-bari/en/05-chiesa-maddalena.mp3',
      },
      transcriptKey: {
        it: 'audioTour.molaDiBari.stops.chiesaMaddalena.transcript',
        en: 'audioTour.molaDiBari.stops.chiesaMaddalena.transcript',
      },
    },
    {
      id: 'palazzo-roberti',
      nameKey: 'audioTour.molaDiBari.stops.palazzoRoberti.name',
      icon: '🏛️',
      imageUrl: '/images/cities/mola-di-bari/palazzo-roberti.jpg',
      lat: 41.0617,
      lon: 17.0903,
      mapUrl: 'https://maps.app.goo.gl/iXLZ75VVpRjqkiQz5',
      audioFile: {
        it: '/audio/tours/mola-di-bari/it/06-palazzo-roberti.mp3',
        en: '/audio/tours/mola-di-bari/en/06-palazzo-roberti.mp3',
      },
      transcriptKey: {
        it: 'audioTour.molaDiBari.stops.palazzoRoberti.transcript',
        en: 'audioTour.molaDiBari.stops.palazzoRoberti.transcript',
      },
    },
    {
      id: 'teatro-van-westerhout',
      nameKey: 'audioTour.molaDiBari.stops.teatroVanWesterhout.name',
      icon: '🎭',
      imageUrl: '/images/cities/mola-di-bari/teatro-van-westerhout.jpg',
      lat: 41.0616,
      lon: 17.0899,
      mapUrl: 'https://maps.app.goo.gl/RfQCjsWfyZJGcfmy9',
      audioFile: {
        it: '/audio/tours/mola-di-bari/it/07-teatro-van-westerhout.mp3',
        en: '/audio/tours/mola-di-bari/en/07-teatro-van-westerhout.mp3',
      },
      transcriptKey: {
        it: 'audioTour.molaDiBari.stops.teatroVanWesterhout.transcript',
        en: 'audioTour.molaDiBari.stops.teatroVanWesterhout.transcript',
      },
    },
    {
      id: 'centro-storico',
      nameKey: 'audioTour.molaDiBari.stops.centroStorico.name',
      icon: '🏘️',
      imageUrl: '/images/cities/mola-di-bari/centro-storico.jpg',
      lat: 41.0623,
      lon: 17.0925,
      mapUrl: 'https://maps.app.goo.gl/1n3yf6sCGdhZxy637',
      audioFile: {
        it: '/audio/tours/mola-di-bari/it/08-centro-storico.mp3',
        en: '/audio/tours/mola-di-bari/en/08-centro-storico.mp3',
      },
      transcriptKey: {
        it: 'audioTour.molaDiBari.stops.centroStorico.transcript',
        en: 'audioTour.molaDiBari.stops.centroStorico.transcript',
      },
    },
    {
      id: 'dona-flor',
      nameKey: 'audioTour.molaDiBari.stops.donaFlor.name',
      icon: '🗿',
      imageUrl: '/images/cities/mola-di-bari/dona-flor.jpg',
      lat: 41.0610,
      lon: 17.0923,
      // Link costruito sulle coordinate della tappa: quello breve portava a Piazza XX Settembre.
      mapUrl: 'https://www.google.com/maps/search/?api=1&query=41.0610,17.0923',
      audioFile: {
        it: '/audio/tours/mola-di-bari/it/09-dona-flor.mp3',
        en: '/audio/tours/mola-di-bari/en/09-dona-flor.mp3',
      },
      transcriptKey: {
        it: 'audioTour.molaDiBari.stops.donaFlor.transcript',
        en: 'audioTour.molaDiBari.stops.donaFlor.transcript',
      },
    },
  ],
};

// Walking route: Valhalla pedestrian routing API - real street waypoints
// Source: valhalla1.openstreetmap.de with costing=pedestrian
// Route: Bar Adriatico → Lungomare Dalmazia → Via C. Colombo → Via V. Veneto →
// Chiesa Matrice → Via Molino → Castello → Via Castello → Via V. Veneto →
// Piazza XX Settembre → Chiesa Maddalena → Via Battisti → Piazza →
// Palazzo Roberti → Via Van Westerhout → Teatro → back through streets →
// Via V. Veneto → Centro Storico → Piazza → Doña Flor
molaDiBariTour.walkingRoute = [
  // Leg 1: Start (Piazza Marinai d'Italia) → Lungomare Dalmazia → Stop 1
  [41.063639, 17.087888],
  [41.063811, 17.087994],
  [41.063804, 17.088088],
  [41.063794, 17.088197],
  [41.063779, 17.088302],
  [41.063751, 17.088415],
  [41.063722, 17.088518],
  [41.063687, 17.088618],
  [41.063640, 17.088719],
  [41.063577, 17.088828],
  [41.063501, 17.088932],
  [41.063375, 17.089081],
  [41.063275, 17.089214],
  [41.063221, 17.089306],
  [41.063173, 17.089409],
  [41.063122, 17.089520],
  [41.063086, 17.089618],
  [41.063049, 17.089737],
  [41.063017, 17.089855],
  [41.062990, 17.089979],
  [41.062974, 17.090091],
  [41.062960, 17.090216],
  [41.062951, 17.090373],
  [41.062960, 17.090505], // ★ STOP 1: Lungomare

  // Leg 2: Lungomare → continues east along Lungomare Dalmazia → turns right into Via Vittorio Veneto (from the top/port side) → Stop 2: Chiesa Matrice
  [41.062964, 17.090556],
  [41.062976, 17.090666],
  [41.063000, 17.090794],
  [41.063025, 17.090914],
  [41.063053, 17.091027],
  [41.063072, 17.091119],
  [41.063097, 17.091259],
  [41.063114, 17.091376],
  [41.063131, 17.091493],
  [41.063146, 17.091614],
  [41.063157, 17.091732],
  [41.063166, 17.091848],
  [41.063172, 17.091980],
  [41.063182, 17.092106],
  [41.063190, 17.092230],
  [41.063198, 17.092356],
  [41.063201, 17.092413],
  [41.063200, 17.092602],
  [41.063195, 17.092671],
  [41.063184, 17.092730],
  [41.063165, 17.092804],
  [41.063144, 17.092863],
  [41.063104, 17.092955],
  [41.063054, 17.093048],
  [41.063000, 17.093130],
  [41.062946, 17.093197],
  [41.062883, 17.093259], // End of Lungomare, turning into Via V. Veneto
  [41.062774, 17.093083],
  [41.062664, 17.092938],
  [41.062528, 17.092759],
  [41.062407, 17.092632],
  [41.062350, 17.092559],
  [41.062162, 17.092340], // ★ STOP 2: Chiesa Matrice

  // Leg 3: Chiesa Matrice → Via Molino → Stop 3: Castello Angioino
  [41.062486, 17.091846],
  [41.062414, 17.091723],
  [41.062304, 17.091635],
  [41.062262, 17.091597],
  [41.062260, 17.091568],
  [41.062295, 17.091500], // ★ STOP 3: Castello Angioino

  // Leg 4: Castello → Via Castello → Via V. Veneto → Piazza → Stop 4
  [41.062260, 17.091568],
  [41.062304, 17.091635],
  [41.061977, 17.092126],
  [41.061825, 17.091964],
  [41.061763, 17.091899],
  [41.061637, 17.091763],
  [41.061123, 17.092583],
  [41.061104, 17.092703],
  [41.061056, 17.092700],
  [41.061023, 17.092688],
  [41.060711, 17.092345],
  [41.060887, 17.092052],
  [41.061213, 17.091508],
  [41.061266, 17.091567],
  [41.061330, 17.091601],
  [41.061399, 17.091607],
  [41.061435, 17.091596], // ★ STOP 4: Piazza XX Settembre

  // Leg 5: Piazza → Corso Umberto → Stop 5: Chiesa Maddalena
  [41.061399, 17.091607],
  [41.061330, 17.091601],
  [41.061266, 17.091567],
  [41.061213, 17.091508],
  [41.061187, 17.091456],
  [41.061171, 17.091403],
  [41.061164, 17.091361],
  [41.061165, 17.091286],
  [41.061181, 17.091202],
  [41.061209, 17.091122],
  [41.061249, 17.091064],
  [41.061177, 17.091038],
  [41.060867, 17.090824],
  [41.060778, 17.090782], // ★ STOP 5: Chiesa Maddalena

  // Leg 6: Chiesa Maddalena → Via Battisti → Piazza → Stop 6: Palazzo Roberti
  [41.060867, 17.090824],
  [41.061177, 17.091038],
  [41.061249, 17.091064],
  [41.061229, 17.090996],
  [41.061225, 17.090928],
  [41.061312, 17.090441],
  [41.061450, 17.089941],
  [41.061645, 17.090092],
  [41.061724, 17.090133],
  [41.061769, 17.090177], // ★ STOP 6: Palazzo Roberti

  // Leg 7: Palazzo Roberti → Via Van Westerhout → Stop 7: Teatro
  [41.061724, 17.090133],
  [41.061645, 17.090092],
  [41.061450, 17.089941],
  [41.061516, 17.089717],
  [41.061639, 17.089811], // ★ STOP 7: Teatro Van Westerhout

  // Leg 8: Teatro → back Via Van Westerhout → Via De Marco → Via Colombo → Via V. Veneto → Stop 8: Centro Storico
  [41.061516, 17.089717],
  [41.061450, 17.089941],
  [41.061645, 17.090092],
  [41.061724, 17.090133],
  [41.062002, 17.090408],
  [41.061962, 17.090504],
  [41.061890, 17.090625],
  [41.061908, 17.090730],
  [41.062009, 17.090839],
  [41.062052, 17.090912],
  [41.061970, 17.091035],
  [41.061667, 17.091696],
  [41.061637, 17.091763],
  [41.061763, 17.091899],
  [41.061825, 17.091964],
  [41.061977, 17.092126],
  [41.062013, 17.092167],
  [41.062162, 17.092339],
  [41.062300, 17.092500], // ★ STOP 8: Centro Storico

  // Leg 9: Centro Storico → Via V. Veneto → Piazza → Stop 9: Doña Flor
  [41.062162, 17.092339],
  [41.062013, 17.092167],
  [41.061977, 17.092126],
  [41.061825, 17.091964],
  [41.061763, 17.091899],
  [41.061637, 17.091763],
  [41.061123, 17.092583],
  [41.061104, 17.092703],
  [41.061056, 17.092700],
  [41.061023, 17.092688],
  [41.060711, 17.092345],
  [41.060840, 17.092131], // ★ STOP 9: Statua di Doña Flor
];

// Registry of all tours
export const audioTours: Record<string, AudioTourConfig> = {
  'mola-di-bari': molaDiBariTour,
};
